// AGC: Tour Guide Provider for Interactive Guided Tour system (v6.9.0)
// AGC: Manages tour state, auto-advance timing, and coordinates with react-joyride

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { CallBackProps, STATUS, EVENTS, ACTIONS } from 'react-joyride'
import { type TourId, allTours, getTourById } from '../config/tourSteps'
import { usePlayerPreferences } from './PlayerPreferencesProvider'

// AGC: Tour state interface
interface TourState {
  isRunning: boolean
  activeTourId: TourId | null
  currentStepIndex: number
  isAutoAdvancing: boolean // True on first watch (3-second auto-advance)
  showTourSelection: boolean // Show tour picker modal
}

// AGC: Tour context value interface
interface TourGuideContextValue {
  // State
  state: TourState
  isFirstTime: boolean // True if user hasn't completed any tour
  
  // Actions
  startTour: (tourId: TourId, initialStepIndex?: number) => void // AGC: Added optional initialStepIndex
  stopTour: () => void
  nextStep: () => void
  prevStep: () => void
  goToStep: (index: number) => void
  skipTour: () => void
  
  // Tour selection modal
  openTourSelection: () => void
  closeTourSelection: () => void
  
  // react-joyride callback handler
  handleJoyrideCallback: (data: CallBackProps) => void
  
  // Utilities
  getTotalSteps: () => number
  getProgressPercent: () => number
  completeTour: (tourId: TourId) => void
}

const TourGuideContext = createContext<TourGuideContextValue | undefined>(undefined)

// AGC: Auto-advance delay in milliseconds (3 seconds)
const AUTO_ADVANCE_DELAY = 3000

export const TourGuideProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { preferences, updatePreferences } = usePlayerPreferences()
  
  // AGC: Tour state
  const [state, setState] = useState<TourState>({
    isRunning: false,
    activeTourId: null,
    currentStepIndex: 0,
    isAutoAdvancing: false,
    showTourSelection: false,
  })
  
  // AGC: Auto-advance timer ref
  const autoAdvanceTimerRef = React.useRef<NodeJS.Timeout | null>(null)

  // AGC: Check if this is first time (no completed tours)
  const isFirstTime = useMemo(() => {
    const completedTours = preferences.completedTours ?? []
    return !preferences.hasCompletedTour && completedTours.length === 0
  }, [preferences.hasCompletedTour, preferences.completedTours])

  // AGC: Get current tour steps
  const currentTourSteps = useMemo(() => {
    if (!state.activeTourId) return []
    const tour = getTourById(state.activeTourId)
    return tour?.steps ?? []
  }, [state.activeTourId])

  // AGC: Get total steps in current tour
  const getTotalSteps = useCallback(() => {
    return currentTourSteps.length
  }, [currentTourSteps])

  // AGC: Get progress percentage
  const getProgressPercent = useCallback(() => {
    const total = getTotalSteps()
    if (total === 0) return 0
    return Math.round(((state.currentStepIndex + 1) / total) * 100)
  }, [state.currentStepIndex, getTotalSteps])

  // AGC: Clear auto-advance timer
  const clearAutoAdvanceTimer = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current)
      autoAdvanceTimerRef.current = null
    }
  }, [])

  // AGC: Start auto-advance timer
  const startAutoAdvanceTimer = useCallback(() => {
    if (!state.isAutoAdvancing || !state.isRunning) return
    
    clearAutoAdvanceTimer()
    
    autoAdvanceTimerRef.current = setTimeout(() => {
      setState((prev) => {
        const nextIndex = prev.currentStepIndex + 1
        const totalSteps = currentTourSteps.length
        
        if (nextIndex >= totalSteps) {
          // AGC: Tour complete
          return { ...prev, isRunning: false, isAutoAdvancing: false }
        }
        
        return { ...prev, currentStepIndex: nextIndex }
      })
    }, AUTO_ADVANCE_DELAY)
  }, [state.isAutoAdvancing, state.isRunning, currentTourSteps.length, clearAutoAdvanceTimer])

  // AGC: Restart auto-advance timer when step changes (if auto-advancing)
  useEffect(() => {
    if (state.isAutoAdvancing && state.isRunning) {
      startAutoAdvanceTimer()
    }
    
    return () => clearAutoAdvanceTimer()
  }, [state.currentStepIndex, state.isAutoAdvancing, state.isRunning, startAutoAdvanceTimer, clearAutoAdvanceTimer])

  // AGC: Start a specific tour with optional initial step index
  const startTour = useCallback((tourId: TourId, initialStepIndex: number = 0) => {
    const tour = getTourById(tourId)
    if (!tour || tour.steps.length === 0) {
      console.warn(`AGC: Tour "${tourId}" not found or has no steps`)
      return
    }
    
    // AGC: Validate initialStepIndex
    const validStepIndex = Math.max(0, Math.min(initialStepIndex, tour.steps.length - 1))
    
    // AGC: Auto-advance only on first time viewing AND starting from step 0
    const completedTours = preferences.completedTours ?? []
    const shouldAutoAdvance = isFirstTime && !completedTours.includes(tourId) && validStepIndex === 0
    
    setState({
      isRunning: true,
      activeTourId: tourId,
      currentStepIndex: validStepIndex, // AGC: Start from specified step
      isAutoAdvancing: shouldAutoAdvance,
      showTourSelection: false,
    })
    
    // AGC: Dispatch navigation event for tours that require specific pages
    // AGC: Only navigate to 'games' if starting from step 0, otherwise navigate based on step
    if (tourId === 'battle') {
      if (validStepIndex >= 15) {
        // AGC: Step 16+ (simulation steps) - navigate to newSimulation
        window.dispatchEvent(new CustomEvent('tour:navigate-to-page', { 
          detail: { page: 'newSimulation' } 
        }))
      } else if (validStepIndex === 0) {
        // AGC: Battle tour step 0 needs to be on Games page
        window.dispatchEvent(new CustomEvent('tour:navigate-to-page', { 
          detail: { page: 'games' } 
        }))
      }
    }
    
    console.log(`AGC: Starting tour "${tourId}" from step ${validStepIndex} with ${tour.steps.length} steps. Auto-advance: ${shouldAutoAdvance}`)
  }, [isFirstTime, preferences.completedTours])

  // AGC: Stop current tour
  const stopTour = useCallback(() => {
    clearAutoAdvanceTimer()
    setState((prev) => ({
      ...prev,
      isRunning: false,
      activeTourId: null,
      currentStepIndex: 0,
      isAutoAdvancing: false,
    }))
  }, [clearAutoAdvanceTimer])

  // AGC: Mark a tour as completed (moved before nextStep to avoid circular dependency)
  const completeTour = useCallback((tourId: TourId) => {
    const completedTours = preferences.completedTours ?? []
    const newCompletedTours = [...completedTours]
    if (!newCompletedTours.includes(tourId)) {
      newCompletedTours.push(tourId)
    }
    
    updatePreferences({
      completedTours: newCompletedTours,
      hasCompletedTour: true,
    })
    
    console.log(`AGC: Tour "${tourId}" completed. Total completed: ${newCompletedTours.length}`)
  }, [preferences.completedTours, updatePreferences])

  // AGC: Track if nextStep is in progress to prevent double calls
  const isAdvancingRef = React.useRef(false)

  // AGC: Go to next step
  const nextStep = useCallback(() => {
    // AGC: Prevent double advancement (e.g., when both custom handler and Joyride callback fire)
    if (isAdvancingRef.current) {
      console.log('AGC: nextStep() skipped - already advancing')
      return
    }
    
    // AGC: Disable auto-advance when user manually navigates
    clearAutoAdvanceTimer()
    
    // AGC: Set flag to prevent double calls
    isAdvancingRef.current = true
    
    setState((prev) => {
      const nextIndex = prev.currentStepIndex + 1
      const totalSteps = currentTourSteps.length
      
      console.log(`AGC: nextStep() called - current: ${prev.currentStepIndex}, next: ${nextIndex}, total: ${totalSteps}`)
      
      if (nextIndex >= totalSteps) {
        // AGC: Mark tour as complete
        if (prev.activeTourId) {
          completeTour(prev.activeTourId)
          
          // AGC: Chain navigation tour to battle tour
          if (prev.activeTourId === 'navigation') {
            console.log('AGC: Navigation tour completed - starting battle tour...')
            // AGC: Navigate to Games page and start battle tour after a delay
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('tour:navigate-to-page', { 
                detail: { page: 'games' } 
              }))
              // AGC: Start battle tour after navigation
              setTimeout(() => {
                setState({
                  isRunning: true,
                  activeTourId: 'battle',
                  currentStepIndex: 0,
                  isAutoAdvancing: false,
                  showTourSelection: false,
                })
                isAdvancingRef.current = false
              }, 500)
            }, 300)
            return { ...prev, isRunning: false, isAutoAdvancing: false }
          }
          
          // AGC: Chain battle tour to blockchain tour
          if (prev.activeTourId === 'battle') {
            console.log('AGC: Battle tour completed - starting blockchain tour...')
            // AGC: Open blockchain tour slides after a delay
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('blockchain-tour:open'))
              isAdvancingRef.current = false
            }, 300)
            return { ...prev, isRunning: false, isAutoAdvancing: false }
          }
        }
        return { ...prev, isRunning: false, isAutoAdvancing: false }
      }
      
      return { ...prev, currentStepIndex: nextIndex, isAutoAdvancing: false }
    })
    
    // AGC: Reset flag after a short delay to allow the step to render
    setTimeout(() => {
      isAdvancingRef.current = false
    }, 100)
  }, [currentTourSteps.length, clearAutoAdvanceTimer, completeTour])

  // AGC: Go to previous step
  const prevStep = useCallback(() => {
    clearAutoAdvanceTimer()
    
    setState((prev) => ({
      ...prev,
      currentStepIndex: Math.max(0, prev.currentStepIndex - 1),
      isAutoAdvancing: false,
    }))
  }, [clearAutoAdvanceTimer])

  // AGC: Go to specific step
  const goToStep = useCallback((index: number) => {
    clearAutoAdvanceTimer()
    
    setState((prev) => ({
      ...prev,
      currentStepIndex: Math.max(0, Math.min(index, currentTourSteps.length - 1)),
      isAutoAdvancing: false,
    }))
  }, [currentTourSteps.length, clearAutoAdvanceTimer])

  // AGC: Skip current tour
  const skipTour = useCallback(() => {
    clearAutoAdvanceTimer()
    
    setState((prev) => ({
      ...prev,
      isRunning: false,
      activeTourId: null,
      currentStepIndex: 0,
      isAutoAdvancing: false,
    }))
  }, [clearAutoAdvanceTimer])

  // AGC: Open tour selection modal
  const openTourSelection = useCallback(() => {
    setState((prev) => ({ ...prev, showTourSelection: true }))
  }, [])

  // AGC: Close tour selection modal
  const closeTourSelection = useCallback(() => {
    setState((prev) => ({ ...prev, showTourSelection: false }))
  }, [])

  // AGC: Handle react-joyride callback
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status, type, action, index } = data
    
    // AGC: Handle errors gracefully - don't close tour on "target not mounted" errors
    if (type === 'error:target_not_found' || type === 'error:target_not_mounted') {
      console.warn(`AGC: Tour target not found for step ${index}, but continuing...`)
      // AGC: Don't close tour, just log the warning
      return
    }
    
    // AGC: Handle tour completion or skip
    if (status === 'finished' || status === 'skipped') {
      if (state.activeTourId && status === 'finished') {
        completeTour(state.activeTourId)
        
        // AGC: Chain navigation tour to battle tour
        if (state.activeTourId === 'navigation') {
          console.log('AGC: Navigation tour finished via Joyride - starting battle tour...')
          stopTour()
          // AGC: Navigate to Games page and start battle tour after a delay
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('tour:navigate-to-page', { 
              detail: { page: 'games' } 
            }))
            // AGC: Start battle tour after navigation
            setTimeout(() => {
              setState({
                isRunning: true,
                activeTourId: 'battle',
                currentStepIndex: 0,
                isAutoAdvancing: false,
                showTourSelection: false,
              })
            }, 500)
          }, 300)
          return
        }
        
        // AGC: Chain battle tour to blockchain tour
        if (state.activeTourId === 'battle') {
          console.log('AGC: Battle tour finished via Joyride - starting blockchain tour...')
          stopTour()
          // AGC: Open blockchain tour slides after a delay
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('blockchain-tour:open'))
          }, 300)
          return
        }
      }
      stopTour()
      return
    }
    
    // AGC: Handle step navigation from joyride
    if (type === 'step:after') {
      if (action === 'next') {
        nextStep()
      } else if (action === 'prev') {
        prevStep()
      }
    }
    
    // AGC: Handle close button click
    if (action === 'close') {
      skipTour()
    }
  }, [state.activeTourId, completeTour, stopTour, nextStep, prevStep, skipTour])

  // AGC: Context value
  const value = useMemo<TourGuideContextValue>(() => ({
    state,
    isFirstTime,
    startTour,
    stopTour,
    nextStep,
    prevStep,
    goToStep,
    skipTour,
    openTourSelection,
    closeTourSelection,
    handleJoyrideCallback,
    getTotalSteps,
    getProgressPercent,
    completeTour,
  }), [
    state,
    isFirstTime,
    startTour,
    stopTour,
    nextStep,
    prevStep,
    goToStep,
    skipTour,
    openTourSelection,
    closeTourSelection,
    handleJoyrideCallback,
    getTotalSteps,
    getProgressPercent,
    completeTour,
  ])

  return (
    <TourGuideContext.Provider value={value}>
      {children}
    </TourGuideContext.Provider>
  )
}

// AGC: Hook to use tour guide context
export const useTourGuide = (): TourGuideContextValue => {
  const context = useContext(TourGuideContext)
  if (!context) {
    throw new Error('useTourGuide must be used within a TourGuideProvider')
  }
  return context
}

export default TourGuideProvider

